// Store Imports
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'
// Third party imports
import { jwtDecode } from 'jwt-decode'
// Shared Imports
import { ELocalStorageKeys, ERoles, ECountry } from '@shared/models'

export interface AuthState {
	logged: boolean
	accessToken: string | null
	refreshToken: string | null
	roles: ERoles[]
	country: ECountry
	userName: string | null
}

const initialState: AuthState = {
	logged: false,
	accessToken: '',
	refreshToken: '',
	roles: [ERoles.user],
	country: ECountry.mexico,
	userName: null,
}

export const AuthStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withMethods((store) => ({
		signin: ({ accessToken, refreshToken }: ISignin): void => {
			window.localStorage.setItem(ELocalStorageKeys.accessToken, accessToken)
			window.localStorage.setItem(ELocalStorageKeys.refreshToken, refreshToken)

			let userName: string | null = null
			try {
				const decoded = jwtDecode<{ name?: string }>(accessToken)
				userName = decoded.name ?? null
			} catch {
				//No-OP
			}

			patchState(store, (state: AuthState) => ({
				logged: true,
				accessToken,
				refreshToken,
				userName,
			}))
		},
		signout: (): void => {
			window.localStorage.removeItem(ELocalStorageKeys.accessToken)
			window.localStorage.removeItem(ELocalStorageKeys.refreshToken)

			patchState(store, (state: AuthState) => initialState)
		},
		updateAccessAndRefreshToken: ({ accessToken, refreshToken }: ISignin): void => {
			window.localStorage.setItem(ELocalStorageKeys.accessToken, accessToken)
			window.localStorage.setItem(ELocalStorageKeys.refreshToken, refreshToken)

			patchState(store, (state: AuthState) => ({ accessToken, refreshToken }))
		},
		updateCountry: ({ country }: { country: ECountry }): void => {
			patchState(store, (state: AuthState) => ({ country }))
		},
		updateRoles: ({ roles }: { roles: ERoles[] }): void => {
			patchState(store, (state: AuthState) => ({ roles }))
		},
		hydrateRolesFromTokenOrLocalStorage: (): void => {
			const roles: ERoles[] = [ERoles.user]

			const scopeRoleMap: Record<string, ERoles> = {
				OMS_USER: ERoles.user,
				OMS_REST_WORK_ADMIN: ERoles.restWorkAdministrator,
				OMS_ADMIN: ERoles.administrator,
				OMS_ALERTS_VIEWER: ERoles.alertsViewer,
				OMS_ALERTS_IMAGE_VIEWER: ERoles.alertsImageViewer,
				OMS_CREATE_SHIFT: ERoles.shiftCreate,
				OMS_SHIFT_DASHBOARD: ERoles.shiftDashboard,
				DEVELOPER: ERoles.developer,
				OMS_SAMSARA: ERoles.samsara,
				OMS_INSTRUCTOR: ERoles.instructor,
				OMS_RUN_SAMSARA_ICS_SYNC_JOB: ERoles.runSamsaraIcsSyncJob,
			}

			const accessToken = window.localStorage.getItem(ELocalStorageKeys.accessToken)
			let userName: string | null = null
			if (accessToken) {
				try {
					const decodedToken = jwtDecode<{ scope?: string[]; name?: string }>(accessToken)
					userName = decodedToken.name ?? null

					if (decodedToken.scope && Array.isArray(decodedToken.scope)) {
						decodedToken.scope.forEach((scopeRole: string) => {
							const mappedRole = scopeRoleMap[scopeRole]
							if (mappedRole && !roles.includes(mappedRole)) {
								roles.push(mappedRole)
							}
						})
					}
				} catch (error) {
					console.error('Error decoding token:', error)
				}
			}

			patchState(store, (state: AuthState) => ({ roles, userName }))
		},
	}))
)

interface ISignin {
	accessToken: string
	refreshToken: string
}
